import React, { useCallback, useEffect, useMemo } from 'react'
import VendorAccount from '../../components/VendorAccount'
import ConnectedVendorAccountOptions from './components/ConnectedVendorAccountOptions'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useCreateDSPAudience } from '../../api/dealership/dsp/audiences/useCreateDSPAudience'
import { get, isEmpty } from 'lodash'
import { useGetMappedAudiences } from '../../api/dealership/customers/audiences/useGetMappedAudiences'
import { toast } from 'react-hot-toast'
import { VENDOR } from '../../lib/utils/vendors'
import { useDealership } from '../../store/zustand/dealershipStore'
import { useCheckFacebookCustomAudienceTOSSigned } from '../../api/dealership/dsp/adAccounts/useCheckFacebookCustomAudienceTOSSigned'
import { Button } from '@demandlocal/components'
import { useGetLinkedAccounts } from '../../api/agencies/useGetLinkedAccounts'
import { GA4 } from '../../lib/utils/ga4'
import { useLocation, useNavigate, useParams } from 'react-router'
import mtm from '../../lib/utils/mtm'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { queryClient } from '../../api/queryClient'

type Props = {
  audience_id?: string
}

const UploadAudience = (props: Props): JSX.Element => {
  const { audience_id } = useParams()
  const { state } = useLocation()
  const dealership = useDealership()
  const navigate = useNavigate()
  const { t } = useTranslation('routes')

  const { mutateAsync: checkFacebookTOSSigned, isLoading: isCheckingFBTos } =
    useCheckFacebookCustomAudienceTOSSigned()

  const formContext = useForm({
    mode: 'onSubmit',
  })
  const {
    watch,
    handleSubmit,
    setError,
    trigger,
    formState: { isSubmitting, isValid },
  } = formContext

  const facebookAccounts = watch('vendor_3')

  useEffect(() => {
    if (!audience_id) navigate(t('dealership.audiences.custom', { dealership }))
  }, [navigate, audience_id, t, dealership])

  useEffect(() => {
    if (facebookAccounts && facebookAccounts?.length > 0) {
      const promises = facebookAccounts
        .map((v: { value: string }) => v.value)
        .map((v: string) => {
          return checkFacebookTOSSigned({
            account_id: v,
          })
        })
      Promise.all(promises).then((resp) => {
        const accounts = resp.reduce((acc, cur) => {
          if (!cur?.tos_accepted?.custom_audience_tos) acc.push(cur)

          return acc
        }, [])
        if (accounts.length > 0) {
          setError('vendor_3', {
            type: 'manual',
            // @ts-ignore
            message: (
              <div className={'flex flex-col gap-4'}>
                {accounts.map((data: { id: string }) => {
                  const account = facebookAccounts?.find(
                    (v: { value: string }) => v.value === data?.id,
                  )
                  return (
                    <div key={data.id}>
                      Facebook Custom Audience TOS not accepted for {account?.label} (
                      {account?.value}). To create or edit an audience with an uploaded
                      customer list, please agree to the Custom Audience terms at{' '}
                      <a
                        className={'font-bold text-primary-persian-blue'}
                        href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=
                        ${data?.id?.replace('act_', '')}`}
                        target={'_blank'}
                        rel="noreferrer">
                        https://business.facebook.com/ads/manage/customaudiences/tos/?act=
                        {data?.id?.replace('act_', '')}
                      </a>{' '}
                      and then try again.
                    </div>
                  )
                })}
              </div>
            ),
          })
        } else {
          trigger('vendor_3')
        }
      })
    } else {
      trigger('vendor_3')
    }
  }, [facebookAccounts, checkFacebookTOSSigned, setError, trigger])

  const { mutateAsync: createDSPAudience } = useCreateDSPAudience()
  const { data: mappedAudiences, isLoading: isLoadingMappedAudiences } =
    useGetMappedAudiences(audience_id ? audience_id : state.audience_id)

  const { data: vendors } = useGetLinkedAccounts()
  const linkedVendorsLength = useMemo(
    () => vendors?.filter((vendor) => vendor.connected).length ?? 0,
    [vendors],
  )
  const unlinkedVendorsLength = useMemo(
    () => vendors?.filter((vendor) => !vendor.connected).length ?? 0,
    [vendors],
  )

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const facebookAccounts = get(values, 'vendor_3', undefined)
    const googleAccounts = get(values, 'vendor_1', undefined)
    const mailchimpAccounts = get(values, 'vendor_4', undefined)
    const tradeDeskAccounts = get(values, 'vendor_11', undefined)
    const centroAccounts = get(values, 'vendor_19', undefined)
    const amazonAccounts = get(values, 'vendor_22', undefined)
    const brevoAccounts = get(values, 'vendor_23', undefined)
    const bingAccounts = get(values, 'vendor_2', undefined)

    GA4.sendEvent({
      event_name: 'create_audience',
      event_params: { audience_id: audience_id },
    })
    mtm.sendEvent('Create', 'Audience', undefined, audience_id)

    if (facebookAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: facebookAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.Facebook,
        },
      })
    }
    if (bingAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: bingAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.Bing,
        },
      })
    }
    if (googleAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: googleAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.Google,
        },
      })
    }
    if (mailchimpAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: mailchimpAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.Mailchimp,
        },
      })
    }
    if (tradeDeskAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: tradeDeskAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.TradeDesk,
        },
      })
    }
    if (centroAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: centroAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.Centro,
        },
      })
    }
    if (amazonAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: amazonAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.AmazonCognition,
        },
      })
    }
    if (brevoAccounts) {
      await createDSPAudience({
        data: {
          audience_id: audience_id,
          vendor_account_ids: brevoAccounts?.map(
            (v: {
              value: string
              label: string
              push_frequency: { label: string; value: string } | undefined
            }) => ({
              id: v.value,
              name: v.label,
              push_frequency: v.push_frequency,
            }),
          ),
          vendor_id: VENDOR.Brevo,
        },
      })
    }
    await queryClient.refetchQueries(['dealership', dealership?.id, 'audiences'])
    toast.success('Audience successfully pushed!')
    navigate(t('dealership.audiences.custom', { dealership }))
  }

  const getMappedAudiencesByVendorId = useCallback(
    (vendor_id) => {
      return (
        mappedAudiences?.filter(
          (audience: { vendor_id: string }) => audience?.vendor_id === vendor_id,
        ) ?? []
      )
    },
    [mappedAudiences],
  )

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col overflow-auto'}>
          <span className={'font-bold text-[28px] text-primary-xiketic'}>
            Upload Audience
          </span>
          <div className={'flex flex-1 flex-col pt-14'}>
            {linkedVendorsLength > 0 && (
              <div className={'pb-10'}>
                <span className={'font-bold text-base'}>Current linked accounts</span>
                <div className={'pt-8'}>
                  {vendors
                    ?.filter((vendor) => vendor.connected)
                    ?.filter((vendor) => {
                      if (vendor.id === VENDOR.AmazonCognition)
                        return !isEmpty(dealership?.amazon_ads_account_id)

                      return (
                        vendor.id !== VENDOR.Elead && vendor.id !== VENDOR.GoogleAnalytics
                      )
                    })
                    .map((vendor, index) => {
                      return (
                        <div key={vendor.id} className={`${index > 0 ? 'mt-10' : ''}`}>
                          <ConnectedVendorAccountOptions
                            mappedAudiences={getMappedAudiencesByVendorId(vendor.id)}
                            vendor={vendor}
                            key={vendor.id}
                            isLoading={isLoadingMappedAudiences}
                          />
                        </div>
                      )
                    })}
                </div>
              </div>
            )}
            {unlinkedVendorsLength > 0 && (
              <div
                className={classNames({
                  'pt-10 border-t-2': linkedVendorsLength > 0,
                  'pt-4': linkedVendorsLength === 0,
                })}>
                <span className={'font-bold text-base'}>
                  Link {linkedVendorsLength === 0 ? 'your' : 'other'} accounts
                </span>
                <div className={'flex grid grid-cols-2 gap-5 mt-6'}>
                  {vendors
                    ?.filter((account) => !account.connected)
                    ?.filter(
                      (vendor) =>
                        vendor.id !== VENDOR.Elead &&
                        vendor.id !== VENDOR.GoogleAnalytics,
                    )
                    ?.map((vendor) => {
                      return <VendorAccount key={vendor.id} vendor={vendor} />
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 mt-4'}>
          <Button
            animate={false}
            variant={'secondary'}
            disabled={isSubmitting || isCheckingFBTos}
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.audiences.custom', { dealership }))
            }}>
            Close
          </Button>
          <Button
            animate={false}
            disabled={isSubmitting || isCheckingFBTos || !isValid}
            type={'submit'}>
            Push audience
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default UploadAudience
